import { createResolver, linkKey, statblockOf, type NoteDetail, type ResolvedLinkTarget } from '@kartothek/shared';
import { prepareForRender, remarkWikilinks, sanitizeSchema } from '@kartothek/shared/markdown';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { useNoteIndex, useNote } from '../hooks';
import { t } from '../i18n';
import { slugify } from '../util';
import { NoteLink, UnresolvedLink } from './NoteLink';
import { StatblockView } from './Statblock';

interface RenderCtx {
  cid: string;
  links: Record<string, ResolvedLinkTarget>;
  folder: string;
  depth: number;
  /** IDs der bereits eingebetteten Notizen (Schleifenschutz). */
  chain: string[];
  isGameMaster: boolean;
  /** Keine Hover-Vorschau in Vorschauen selbst. */
  preview: boolean;
}

const Ctx = createContext<RenderCtx | null>(null);

function useRenderCtx(): RenderCtx {
  return useContext(Ctx)!;
}

/** Liefert das Ziel eines Links: zuerst Server-Auflösung, sonst lokal über den Notiz-Index. */
function useResolve() {
  const ctx = useRenderCtx();
  const index = useNoteIndex(ctx.cid);
  const resolver = useMemo(
    () => createResolver((index.data ?? []).map((n) => ({ id: n.id, folder: n.folder, title: n.title }))),
    [index.data],
  );
  return (target: string): ResolvedLinkTarget | null => {
    const hit = ctx.links[linkKey(target)];
    if (hit) return hit;
    const r = resolver.resolve(target, ctx.folder);
    if (r?.type === 'note') {
      const n = index.data?.find((x) => x.id === r.id);
      if (n) return { type: 'note', id: n.id, title: n.title, folder: n.folder, typeKey: n.typeKey };
    }
    return null;
  };
}

function WikiLinkEl(props: { 'data-target'?: string; 'data-heading'?: string; children?: ReactNode }) {
  const ctx = useRenderCtx();
  const resolve = useResolve();
  const target = props['data-target'] ?? '';
  const heading = props['data-heading'];
  if (!target && heading) {
    return (
      <a className="wikilink" href={`#${slugify(heading)}`}>
        {props.children}
      </a>
    );
  }
  const r = resolve(target);
  if (r?.type === 'note') {
    return (
      <NoteLink cid={ctx.cid} noteId={r.id} heading={heading} preview={!ctx.preview}>
        {props.children}
      </NoteLink>
    );
  }
  if (r?.type === 'file') {
    return (
      <a className="wikilink" href={r.url} target="_blank" rel="noreferrer">
        {props.children}
      </a>
    );
  }
  return (
    <UnresolvedLink cid={ctx.cid} target={target}>
      {props.children}
    </UnresolvedLink>
  );
}

function WikiEmbedEl(props: { 'data-target'?: string; 'data-heading'?: string; 'data-width'?: string; 'data-alias'?: string }) {
  const ctx = useRenderCtx();
  const resolve = useResolve();
  const target = props['data-target'] ?? '';
  const r = resolve(target);
  const width = props['data-width'] ? Number(props['data-width']) : undefined;
  if (r?.type === 'file') return <FileEmbed file={r} width={width} alt={props['data-alias'] ?? r.name} />;
  if (r?.type === 'note') {
    if (ctx.chain.includes(r.id) || ctx.depth >= 2) {
      return <div className="embed embed-skipped">{t('note.embedLoop')}</div>;
    }
    return <NoteEmbed noteId={r.id} heading={props['data-heading']} />;
  }
  return (
    <UnresolvedLink cid={ctx.cid} target={target}>
      {target}
    </UnresolvedLink>
  );
}

function FileEmbed({ file, width, alt }: { file: Extract<ResolvedLinkTarget, { type: 'file' }>; width?: number; alt: string }) {
  if (file.mime.startsWith('image/')) {
    return <img className="embed-image" src={file.url} alt={alt} width={width} loading="lazy" />;
  }
  if (file.mime.startsWith('audio/')) return <audio controls src={file.url} />;
  if (file.mime.startsWith('video/')) return <video controls src={file.url} width={width} />;
  return (
    <a href={file.url} target="_blank" rel="noreferrer">
      {file.name}
    </a>
  );
}

/** Schneidet den Abschnitt unter einer Überschrift aus (für `![[Notiz#Abschnitt]]`). */
function extractSection(body: string, heading: string): string {
  const lines = body.split('\n');
  const want = heading.trim().toLowerCase();
  const start = lines.findIndex((l) => /^#{1,6}\s/.test(l) && l.replace(/^#{1,6}\s+/, '').trim().toLowerCase() === want);
  if (start === -1) return body;
  const level = /^(#+)/.exec(lines[start]!)![1]!.length;
  let end = lines.length;
  for (let i = start + 1; i < lines.length; i++) {
    const m = /^(#{1,6})\s/.exec(lines[i]!);
    if (m && m[1]!.length <= level) {
      end = i;
      break;
    }
  }
  return lines.slice(start, end).join('\n');
}

function NoteEmbed({ noteId, heading }: { noteId: string; heading?: string }) {
  const ctx = useRenderCtx();
  const q = useNote(ctx.cid, noteId);
  if (!q.data) return <div className="embed muted">{t('common.loading')}</div>;
  const note = q.data;
  const body = heading ? extractSection(note.body, heading) : note.body;
  // Eingebettete Kreaturen zeigen ihren Statblock (z. B. Gegner im Raum)
  const sb = heading ? null : statblockOf(note.frontmatter);
  return (
    <div className="embed embed-note">
      <div className="embed-title">
        <NoteLink cid={ctx.cid} noteId={note.id} preview={false}>
          {note.title}
        </NoteLink>
      </div>
      {sb && (
        <StatblockView
          statblock={sb}
          fallbackName={note.title}
          cid={ctx.cid}
          links={note.links}
          folder={note.folder}
          isGameMaster={ctx.isGameMaster}
          compact
        />
      )}
      <MarkdownBody
        cid={ctx.cid}
        body={body}
        links={note.links}
        folder={note.folder}
        depth={ctx.depth + 1}
        chain={[...ctx.chain, note.id]}
        isGameMaster={ctx.isGameMaster}
        preview={ctx.preview}
      />
    </div>
  );
}

function headingComponent(Tag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6') {
  return function Heading({ children }: { children?: ReactNode }) {
    const text = textOf(children);
    return <Tag id={slugify(text)}>{children}</Tag>;
  };
}

function textOf(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (node && typeof node === 'object' && 'props' in node) return textOf((node.props as { children?: ReactNode }).children);
  return '';
}

function MarkdownImg(props: { src?: string; alt?: string; width?: string | number }) {
  const resolve = useResolve();
  const ctx = useRenderCtx();
  const src = props.src ?? '';
  if (!/^(https?:|data:|\/api\/)/i.test(src)) {
    let decoded = src;
    try {
      decoded = decodeURIComponent(src);
    } catch {
      /* unverändert */
    }
    const r = ctx.links[linkKey(decoded)] ?? resolve(decoded);
    if (r?.type === 'file') return <FileEmbed file={r} alt={props.alt ?? r.name} />;
    return <span className="wikilink unresolved">{props.alt || decoded}</span>;
  }
  return <img src={src} alt={props.alt} width={props.width as number | undefined} loading="lazy" />;
}

function MarkdownAnchor(props: { href?: string; children?: ReactNode }) {
  const href = props.href ?? '';
  const ctx = useRenderCtx();
  const resolve = useResolve();
  if (/\.md(#.*)?$/i.test(href) && !/^[a-z]+:/i.test(href)) {
    let decoded = href;
    try {
      decoded = decodeURIComponent(href);
    } catch {
      /* unverändert */
    }
    const [path, heading] = decoded.split('#');
    const r = ctx.links[linkKey(path!)] ?? resolve(path!);
    if (r?.type === 'note') {
      return (
        <NoteLink cid={ctx.cid} noteId={r.id} heading={heading} preview={!ctx.preview}>
          {props.children}
        </NoteLink>
      );
    }
  }
  const external = /^https?:/i.test(href);
  return (
    <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noreferrer noopener' : undefined}>
      {props.children}
    </a>
  );
}

/** DM-Blöcke mit sichtbarer Kennzeichnung. */
function Div(props: { className?: string; children?: ReactNode }) {
  const cls = props.className;
  if (cls === 'dm-secret' || cls === 'dm-comment') {
    return (
      <div className={cls}>
        <span className="secret-label">{t(cls === 'dm-secret' ? 'note.secretLabel' : 'note.commentLabel')}</span>
        {props.children}
      </div>
    );
  }
  return <div className={cls}>{props.children}</div>;
}

const components = {
  div: Div,
  'wiki-link': WikiLinkEl,
  'wiki-embed': WikiEmbedEl,
  img: MarkdownImg,
  a: MarkdownAnchor,
  h1: headingComponent('h1'),
  h2: headingComponent('h2'),
  h3: headingComponent('h3'),
  h4: headingComponent('h4'),
  h5: headingComponent('h5'),
  h6: headingComponent('h6'),
} as unknown as Components;

// Einfache Zeilenumbrüche wie in Obsidian (Standardeinstellung „Strikte Zeilenumbrüche“ aus)
const remarkPlugins = [remarkGfm, remarkBreaks, remarkWikilinks];
const rehypePlugins = [rehypeRaw, [rehypeSanitize, sanitizeSchema]] as NonNullable<Parameters<typeof ReactMarkdown>[0]['rehypePlugins']>;

export function MarkdownBody(props: RenderCtx & { body: string }) {
  const { body, ...ctx } = props;
  const source = useMemo(() => prepareForRender(body, ctx.isGameMaster), [body, ctx.isGameMaster]);
  return (
    <Ctx.Provider value={ctx}>
      <div className="markdown">
        <ReactMarkdown remarkPlugins={remarkPlugins} rehypePlugins={rehypePlugins} components={components}>
          {source}
        </ReactMarkdown>
      </div>
    </Ctx.Provider>
  );
}

export function MarkdownView({ note, isGameMaster, preview = false }: { note: NoteDetail; isGameMaster: boolean; preview?: boolean }) {
  if (!note.body.trim()) return <p className="muted">{t('note.empty')}</p>;
  return (
    <MarkdownBody
      cid={note.campaignId}
      body={note.body}
      links={note.links}
      folder={note.folder}
      depth={0}
      chain={[note.id]}
      isGameMaster={isGameMaster}
      preview={preview}
    />
  );
}
